import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { MessageCircle, Plus, X } from 'lucide-react';
import { ROLES } from '../../lib/routeConfig';
import { ordersApi } from '../../lib/ordersApi';

// Phase 15.5 — Color chip mapping for the "Color" option group. Matching is
// intentionally CASE-SENSITIVE on the exact stored value (Shopify stores
// "blanc" lowercase). Values outside the map render without a chip (graceful
// fallback). "blanc" gets a subtle border so it stays visible on the white
// modale card.
const COLOR_DOT_MAP = {
    'Rouge': '#DC2626',
    'Vert': '#16A34A',
    'Beige': '#D4C5A9',
    'Noir': '#111827',
    'Rose': '#EC4899',
    'Violet': '#7C3AED',
    'Bronze': '#A97142',
    'Bleu': '#2563EB',
    'blanc': '#FFFFFF',
};

function ColorDot({ value }) {
    const hex = COLOR_DOT_MAP[value];
    if (!hex) return null;
    const needsBorder = hex.toUpperCase() === '#FFFFFF';
    return (
        <span
            aria-hidden="true"
            data-testid={`color-dot-${value}`}
            data-color-hex={hex}
            className="inline-block rounded-full shrink-0"
            style={{ width: 12, height: 12, backgroundColor: hex, border: needsBorder ? '1px solid #E5E7EB' : undefined }}
        />
    );
}

// Phase 16 — one item = one Produit Select + N option Selects + Quantité.
// The parent WhatsappOrderDialog owns an array of these blocks. Phase 16.1
// re-uses this same block inside the OrderDetailSheet edit flow, hence the
// `testIdPrefix` prop (defaults to `new-order` to preserve the modal's
// existing testids).
export const MAX_ITEMS = 10;

const emptyItem = () => ({
    product_name: '',
    product_options: {},
    quantity: 1,
});

export function ProductItemBlock({
    index,
    item,
    productCatalog,
    canRemove,
    onChange,
    onRemove,
    autoFocus,
    testIdPrefix = 'new-order',
}) {
    const triggerRef = React.useRef(null);
    React.useEffect(() => {
        if (autoFocus && triggerRef.current) {
            try { triggerRef.current.focus(); } catch { /* noop */ }
        }
    }, [autoFocus]);

    const matchedProduct = React.useMemo(() => {
        const q = (item.product_name || '').trim().toLowerCase();
        if (!q) return null;
        return productCatalog.find((p) => (p.name || '').trim().toLowerCase() === q) || null;
    }, [item.product_name, productCatalog]);

    const optionGroups = matchedProduct?.options || [];

    const setProduct = (v) => onChange({ ...item, product_name: v, product_options: {} });
    const setOption = (name, v) => onChange({ ...item, product_options: { ...item.product_options, [name]: v } });
    const setQuantity = (v) => onChange({ ...item, quantity: v });

    return (
        <div
            data-testid={`${testIdPrefix}-item-${index}`}
            className="relative rounded-lg border border-[#E5E7EB] bg-[#FAFBFC] p-3 space-y-3"
        >
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">
                    Produit {index + 1}
                </span>
                {canRemove && (
                    <button
                        type="button"
                        data-testid={`${testIdPrefix}-item-remove-${index}`}
                        onClick={onRemove}
                        aria-label={`Retirer le produit ${index + 1}`}
                        className="inline-flex items-center gap-1 h-6 px-1.5 rounded text-[11px] text-[#6B7280] hover:text-[#EF4444] hover:bg-[#EF4444]/10"
                    >
                        <X size={12} strokeWidth={2} />
                        Retirer
                    </button>
                )}
            </div>

            <Field label="Produit" required>
                <Select value={item.product_name || ''} onValueChange={setProduct}>
                    <SelectTrigger
                        ref={triggerRef}
                        data-testid={`${testIdPrefix}-product-${index}`}
                        className="h-9 text-sm bg-white"
                    >
                        <SelectValue placeholder="— Sélectionner —" />
                    </SelectTrigger>
                    <SelectContent>
                        {productCatalog.map((p) => (
                            <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>

            {optionGroups.map((g) => {
                const selectedValue = item.product_options[g.name] || '';
                if (g.name === 'Color') {
                    return (
                        <Field key={g.name} label={g.name} required>
                            <Select value={selectedValue} onValueChange={(v) => setOption(g.name, v)}>
                                <SelectTrigger
                                    data-testid={`${testIdPrefix}-option-${index}-${g.name}`}
                                    data-selected-value={selectedValue}
                                    className="h-9 text-sm bg-white"
                                >
                                    {selectedValue ? (
                                        <span className="flex items-center gap-2">
                                            <ColorDot value={selectedValue} />
                                            <span>{selectedValue}</span>
                                        </span>
                                    ) : (
                                        <SelectValue placeholder="— Choisir —" />
                                    )}
                                </SelectTrigger>
                                <SelectContent>
                                    {(g.values || []).map((v) => (
                                        <SelectItem key={v} value={v}>
                                            <span className="flex items-center gap-2">
                                                <ColorDot value={v} />
                                                <span>{v}</span>
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                    );
                }
                return (
                    <Field key={g.name} label={g.name} required>
                        <select
                            data-testid={`${testIdPrefix}-option-${index}-${g.name}`}
                            required
                            value={selectedValue}
                            onChange={(e) => setOption(g.name, e.target.value)}
                            className="input bg-white"
                        >
                            <option value="">— Choisir —</option>
                            {(g.values || []).map((v) => (
                                <option key={v} value={v}>{v}</option>
                            ))}
                        </select>
                    </Field>
                );
            })}

            <Field label="Quantité" required>
                <input
                    data-testid={`${testIdPrefix}-quantity-${index}`}
                    type="number"
                    min="1"
                    required
                    value={item.quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="input bg-white"
                />
            </Field>
        </div>
    );
}


// Phase 16 — New WhatsApp order dialog supports 1..10 line items. Shared
// customer + price + call center fields sit ABOVE the repeatable product
// blocks. On submit:
//   • 1 item  → flat fields (product_name/product_variant/quantity), no
//     line_items in the DB (identical to the pre-Phase 16 shape).
//   • 2+ items → line_items array; flat product fields left null.
// The price is a single MAD total for the whole order — never split per
// item.
export default function NewWhatsappOrderDialog({ open, onOpenChange, user, callCenters, productCatalog = [], onCreated }) {
    const isAgent = user?.role === ROLES.CC_AGENT;
    const [form, setForm] = React.useState(() => ({
        full_name: '', phone: '', city: '', address: '',
        price: '', call_center_id: '',
        items: [emptyItem()],
    }));
    const [busy, setBusy] = React.useState(false);
    const [err, setErr] = React.useState(null);
    const [focusIdx, setFocusIdx] = React.useState(-1);

    React.useEffect(() => {
        if (!open) {
            setForm({
                full_name: '', phone: '', city: '', address: '',
                price: '', call_center_id: '', items: [emptyItem()],
            });
            setErr(null);
            setFocusIdx(-1);
        }
    }, [open]);

    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const updateItem = (idx, next) => {
        setForm((prev) => ({
            ...prev,
            items: prev.items.map((it, i) => i === idx ? next : it),
        }));
    };

    const addItem = () => {
        setForm((prev) => {
            if (prev.items.length >= MAX_ITEMS) return prev;
            const next = { ...prev, items: [...prev.items, emptyItem()] };
            setFocusIdx(next.items.length - 1);
            return next;
        });
    };

    const removeItem = (idx) => {
        setForm((prev) => {
            if (prev.items.length <= 1) return prev;
            return { ...prev, items: prev.items.filter((_, i) => i !== idx) };
        });
        setFocusIdx(-1);
    };

    const itemComplete = (item) => {
        if (!item.product_name) return false;
        const matched = productCatalog.find((p) => (p.name || '').trim().toLowerCase() === (item.product_name || '').trim().toLowerCase());
        const groups = matched?.options || [];
        if (groups.length > 0 && !groups.every((g) => !!item.product_options[g.name])) return false;
        if (!(Number(item.quantity) >= 1)) return false;
        return true;
    };

    const allItemsComplete = form.items.length > 0 && form.items.every(itemComplete);
    const priceValid = Number(form.price) > 0;

    const buildItemPayload = (item) => {
        const matched = productCatalog.find((p) => (p.name || '').trim().toLowerCase() === (item.product_name || '').trim().toLowerCase());
        const groups = matched?.options || [];
        const variant = groups.length > 0
            ? groups.map((g) => item.product_options[g.name]).filter(Boolean).join(' / ')
            : '';
        return {
            product_name: item.product_name.trim(),
            product_variant: variant || null,
            quantity: Number(item.quantity) || 1,
        };
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!allItemsComplete) {
            setErr('Veuillez compléter tous les produits (nom, options, quantité).');
            return;
        }
        if (!priceValid) {
            setErr('Le prix total doit être supérieur à 0.');
            return;
        }
        setBusy(true); setErr(null);
        try {
            const body = {
                full_name: form.full_name.trim(),
                phone: form.phone.trim(),
                city: form.city.trim(),
                price: Number(form.price) || 0,
                line_items: form.items.map(buildItemPayload),
            };
            if (form.address && form.address.trim()) body.address = form.address.trim();
            if (!isAgent) body.call_center_id = form.call_center_id;
            const res = await ordersApi.createWhatsapp(body);
            onCreated?.(res.order);
            onOpenChange(false);
        } catch (e) {
            setErr(typeof e.detail === 'string' ? e.detail : (e.message || 'Erreur'));
        } finally {
            setBusy(false);
        }
    };

    const canSubmit = form.full_name && form.phone && form.city && priceValid && allItemsComplete && (isAgent || form.call_center_id);
    const canAdd = form.items.length < MAX_ITEMS;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="new-whatsapp-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[#1A1D2E]">
                        <MessageCircle size={20} strokeWidth={1.75} className="text-[#10B981]" />
                        Nouvelle commande — WhatsApp
                    </DialogTitle>
                    <DialogDescription className="text-[#6B7280]">
                        Créez une commande manuelle reçue via WhatsApp. Elle sera assignée directement à votre call center, sans passer par la distribution round-robin.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    {/* Shared customer fields */}
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Nom complet" required>
                            <input data-testid="new-order-full-name" required value={form.full_name} onChange={(e) => set('full_name', e.target.value)} className="input" />
                        </Field>
                        <Field label="Téléphone" required>
                            <input data-testid="new-order-phone" required value={form.phone} onChange={(e) => set('phone', e.target.value)} className="input" />
                        </Field>
                        <Field label="Ville" required>
                            <input data-testid="new-order-city" required value={form.city} onChange={(e) => set('city', e.target.value)} className="input" />
                        </Field>
                        <Field label="Adresse">
                            <input data-testid="new-order-address" value={form.address} onChange={(e) => set('address', e.target.value)} className="input" placeholder="Optionnel" />
                        </Field>
                    </div>

                    {/* Repeatable product items */}
                    <div className="space-y-3">
                        {form.items.map((item, idx) => (
                            <ProductItemBlock
                                key={idx}
                                index={idx}
                                item={item}
                                productCatalog={productCatalog}
                                canRemove={idx > 0}
                                onChange={(next) => updateItem(idx, next)}
                                onRemove={() => removeItem(idx)}
                                autoFocus={focusIdx === idx}
                            />
                        ))}
                        {canAdd && (
                            <button
                                type="button"
                                data-testid="new-order-add-item"
                                onClick={addItem}
                                className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg border border-dashed border-[#4B3FD8]/40 bg-white text-sm font-medium text-[#4B3FD8] hover:bg-[#4B3FD8]/5"
                            >
                                <Plus size={14} strokeWidth={2} />
                                Ajouter un autre produit
                            </button>
                        )}
                        {!canAdd && (
                            <div className="text-[11px] text-[#6B7280] text-center">Maximum {MAX_ITEMS} produits par commande.</div>
                        )}
                    </div>

                    {/* Shared price + call center */}
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Prix total" required>
                            <div className="relative">
                                <input data-testid="new-order-price" type="number" step="0.01" min="0" required value={form.price} onChange={(e) => set('price', e.target.value)} className="input pr-12" />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#6B7280]">MAD</span>
                            </div>
                        </Field>
                        {!isAgent && (
                            <Field label="Call Center" required>
                                <select data-testid="new-order-call-center" required value={form.call_center_id} onChange={(e) => set('call_center_id', e.target.value)} className="input">
                                    <option value="">— Choisir —</option>
                                    {callCenters.filter((cc) => cc.is_active !== false).map((cc) => (
                                        <option key={cc.id} value={cc.id}>{cc.name}{cc.commercial_name ? ` — ${cc.commercial_name}` : ''}</option>
                                    ))}
                                </select>
                            </Field>
                        )}
                    </div>

                    {err && <div data-testid="new-order-error" className="text-sm text-[#EF4444]">{err}</div>}
                    <DialogFooter className="mt-4">
                        <button type="button" onClick={() => onOpenChange(false)} className="h-9 px-4 rounded-lg border border-[#E5E7EB] text-sm text-[#1A1D2E] hover:bg-[#F5F6FA]">Annuler</button>
                        <button
                            type="submit"
                            data-testid="new-order-submit"
                            disabled={!canSubmit || busy}
                            className="h-9 px-4 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-medium disabled:opacity-60"
                        >
                            {busy ? 'Création…' : 'Créer la commande'}
                        </button>
                    </DialogFooter>
                </form>
                <style>{`
                    .input { height: 36px; padding: 0 12px; border-radius: 8px; border: 1px solid #E5E7EB; background: #fff; font-size: 14px; color: #1A1D2E; width: 100%; }
                    .input:focus { outline: none; border-color: #4B3FD8; }
                    textarea.input { padding: 8px 12px; }
                `}</style>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block text-xs font-medium text-[#6B7280]">
            <span className="block mb-1">{label}{required && <span className="text-[#EF4444]"> *</span>}</span>
            {children}
        </label>
    );
}
